<template>
  <Transition name="slide">
    <aside
      v-if="showHeader"
      class="sidebar-v2"
      :class="{ 'sidebar-open': mobileSidebarOpen, rail: railMode }"
    >
      <!-- En-tête : logo + repli (ou fermeture sur mobile) -->
      <div class="sb-head">
        <router-link to="/" class="sb-logoblk" @click="$emit('close-mobile')">
          <img src="/logo-alertis.png" alt="Alertis" class="sb-logo" />
          <span class="sb-logotxt">
            Back Office
            <span v-if="isStaging" class="sb-stag">Staging</span>
          </span>
        </router-link>
        <button
          type="button"
          class="sb-ghostbtn sb-toggle"
          :aria-label="railMode ? 'Agrandir la barre de navigation' : 'Réduire la barre de navigation'"
          @click="$emit('toggle-sidebar')"
        >
          <StrokeIcon :paths="railMode ? CHEV_RIGHT : CHEV_LEFT" :size="15" :stroke-width="2" />
          <span>{{ railMode ? 'Agrandir' : 'Réduire' }}</span>
        </button>
        <button
          type="button"
          class="sb-ghostbtn sb-close"
          aria-label="Fermer le menu"
          @click="$emit('close-mobile')"
        >
          <StrokeIcon :paths="X_ICON" :size="15" :stroke-width="2" />
          <span>Fermer</span>
        </button>
      </div>

      <!-- Action rapide : saisir une demande pendant un appel -->
      <router-link
        to="/form"
        class="sb-newbtn"
        :class="{ on: isFormActive }"
        :aria-current="isFormActive ? 'page' : undefined"
        @click="$emit('close-mobile')"
      >
        <StrokeIcon :paths="PLUS_ICON" :size="17" :stroke-width="2.1" />
        <span class="sb-newfull">Nouvelle demande</span>
        <span class="sb-newshort">Nouvelle</span>
      </router-link>

      <AppSidebarV2Navigation
        :collapsed="railMode"
        :is-admin="isAdmin"
        :is-super-admin="isSuperAdmin"
        :has-acces-pointeuse="hasAccesPointeuse"
        :today-tasks-count="todayTasksCount"
        :unread-messages-count="unreadMessagesCount"
        :unread-messages-details="unreadMessagesDetails"
        :pending-veille-count="pendingVeilleCount"
        :to-send-veille-count="toSendVeilleCount"
        :pending-feedback-count="pendingFeedbackCount"
        @close-mobile="$emit('close-mobile')"
      />

      <!-- Pied : notifications + profil, toujours visibles -->
      <div class="sb-foot">
        <AppSidebarV2Notifications :collapsed="railMode" />
        <AppSidebarV2Profile :user="user" :collapsed="railMode" @logout="$emit('logout')" />
      </div>
    </aside>
  </Transition>
</template>

<script setup>
/**
 * Barre de navigation v2 — implémentation du canvas « Barre de navigation v2 »
 * (https://claude.ai/code/artifact/92b34e6d-f436-4738-b722-3ce22ae1ffbe).
 * 290 px déployée / rail 96 px à libellés courts / tiroir 320 px sur mobile.
 * L'accent suit --color-primary : le staging passe tout au vert via
 * :root.is-staging (variables.css), sans code dédié ici.
 * Même contrat de props/événements que l'ancienne barre v1, qu'elle a
 * remplacée.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { StrokeIcon } from '../ui'
import AppSidebarV2Navigation from './AppSidebarV2Navigation.vue'
import AppSidebarV2Notifications from './AppSidebarV2Notifications.vue'
import AppSidebarV2Profile from './AppSidebarV2Profile.vue'
import { useTasksTodayCount } from '../../composables/tasks/useTasksTodayCount.js'
import { useUnreadMessages } from '../../composables/useUnreadMessages.js'
import { usePendingVeilleCount } from '../../composables/usePendingVeilleCount.js'
import { useFeedbackPendingCount } from '../../composables/useFeedbackPendingCount.js'

const props = defineProps({
  showHeader: { type: Boolean, required: true },
  mobileSidebarOpen: { type: Boolean, default: false },
  sidebarCollapsed: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false },
  isSuperAdmin: { type: Boolean, default: false },
  hasAccesPointeuse: { type: Boolean, default: false },
  user: { type: Object, default: null }
})

defineEmits(['close-mobile', 'toggle-sidebar', 'logout'])

const CHEV_LEFT = ['M15 18l-6-6 6-6']
const CHEV_RIGHT = ['M9 18l6-6-6-6']
const X_ICON = ['M18 6L6 18', 'M6 6l12 12']
const PLUS_ICON = ['M12 5v14', 'M5 12h14']

const isStaging = import.meta.env.VITE_IS_STAGING === 'true'

const route = useRoute()
const isFormActive = computed(() => route.path === '/form' || route.path.startsWith('/form/'))

// Sur mobile le tiroir est toujours déployé : le mode rail ne s'applique
// qu'au-dessus de 768 px (le libellé court n'a pas de sens dans un tiroir).
const isMobileViewport = ref(false)
let mediaQuery = null
const onMediaChange = (event) => { isMobileViewport.value = event.matches }
const railMode = computed(() => props.sidebarCollapsed && !isMobileViewport.value)

// Compteurs — mêmes sources que la v1.
const { todayTasksCount, loadTasksCount } = useTasksTodayCount()
const { unreadCount: unreadMessagesCount, unreadDetails: unreadMessagesDetails } = useUnreadMessages()
const { pendingVeilleCount, toSendVeilleCount } = usePendingVeilleCount()
const { pendingFeedbackCount } = useFeedbackPendingCount()

let refreshInterval = null

onMounted(() => {
  mediaQuery = window.matchMedia('(max-width: 768px)')
  isMobileViewport.value = mediaQuery.matches
  mediaQuery.addEventListener('change', onMediaChange)

  loadTasksCount()
  refreshInterval = setInterval(() => {
    loadTasksCount()
  }, 60000)
})

onUnmounted(() => {
  if (mediaQuery) mediaQuery.removeEventListener('change', onMediaChange)
  if (refreshInterval) clearInterval(refreshInterval)
})

watch(() => route.path, (newPath, oldPath) => {
  if (oldPath === '/tasks/today' && newPath !== '/tasks/today') {
    loadTasksCount()
  }
})
</script>

<style scoped>
.sidebar-v2 {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: var(--sidebar-width);
  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-border);
  z-index: var(--z-fixed);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-v2.rail { width: 96px; }

/* ── En-tête ── */
.sb-head {
  padding: 16px 16px 14px;
  border-bottom: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
}

.sb-logoblk {
  min-width: 0;
  text-decoration: none;
  transition: opacity var(--transition-base);
}
.sb-logoblk:hover { opacity: 0.8; }
.sb-logo { height: 30px; width: auto; display: block; }
.sb-logotxt {
  font-size: 12px;
  color: var(--color-text-tertiary);
  margin-top: 5px;
  display: flex;
  gap: 7px;
  align-items: center;
}
.sb-stag {
  background: var(--color-primary);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 9999px;
  line-height: 1.4;
}

.sb-ghostbtn {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-primary);
  border-radius: 6px;
  min-height: 36px;
  padding: 6px 10px;
  font-size: 12.5px;
  font-weight: 500;
  font-family: inherit;
  color: var(--color-text-tertiary);
  cursor: pointer;
  transition: background 150ms, color 150ms, border-color 150ms;
  flex-shrink: 0;
}
.sb-ghostbtn:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  border-color: var(--color-border-dark);
}
.sb-ghostbtn:focus-visible { outline: none; box-shadow: var(--focus-ring); }
.sb-close { display: none; }

.sidebar-v2.rail .sb-head { flex-direction: column; padding: 14px 8px 12px; }
.sidebar-v2.rail .sb-logoblk { display: none; }
.sidebar-v2.rail .sb-toggle { flex-direction: column; gap: 3px; width: 100%; padding: 8px 4px; }

/* ── Bouton Nouvelle demande ── */
.sb-newbtn {
  margin: 14px 16px 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  background: var(--color-primary);
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 150ms;
  flex-shrink: 0;
}
.sb-newbtn:hover { background: var(--color-primary-dark); }
.sb-newbtn.on { background: var(--color-primary-dark); }
.sb-newbtn:focus-visible { outline: none; box-shadow: var(--focus-ring-strong); }
.sb-newshort { display: none; }

.sidebar-v2.rail .sb-newbtn {
  margin: 12px 8px 2px;
  flex-direction: column;
  gap: 3px;
  padding: 8px 4px;
  min-height: 56px;
  font-size: 12px;
}
.sidebar-v2.rail .sb-newfull { display: none; }
.sidebar-v2.rail .sb-newshort { display: inline; }

/* ── Pied ── */
.sb-foot {
  border-top: 1px solid var(--color-border-light);
  padding: 8px 12px 12px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
}
.sidebar-v2.rail .sb-foot { padding: 8px 8px 12px; }

/* ── Transition d'apparition (connexion/déconnexion), comme la v1 ── */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
}
.slide-enter-from { transform: translateX(-100%); opacity: 0; }
.slide-enter-to { transform: translateX(0); opacity: 1; }
.slide-leave-from { transform: translateX(0); opacity: 1; }
.slide-leave-to { transform: translateX(-100%); opacity: 0; }

/* ── Mobile : tiroir 320 px ── */
@media (max-width: 768px) {
  .sidebar-v2,
  .sidebar-v2.rail {
    width: 320px;
    max-width: 85vw;
    transform: translateX(-100%);
  }
  .sidebar-v2.sidebar-open {
    transform: translateX(0);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .sb-toggle { display: none; }
  .sb-close { display: flex; }
}
</style>
